/**
 * Consent claim — opens from an institution's magic link (SMS/email).
 * Reads `?t=<claim_token>` from the URL, calls /api/consent/claim to bind
 * the pending request to this device_id, then redirects to the detail page.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { claimConsent } from "@/lib/consent";
import { emitSignal } from "@/lib/wallet-signal";

export default function WalletConsentClaim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    const token = params.get("t");
    if (!token) {
      setTokenMissing(true);
      return;
    }
    emitSignal("wallet.consent.claim_opened");
    claimConsent(token)
      .then((res) => {
        emitSignal("wallet.consent.claim_success", { consent_id: res?.consent?.id });
        navigate(`/wallet/consent/${res.consent.id}`, { replace: true });
      })
      .catch((e) => {
        emitSignal("wallet.consent.claim_failed", { detail: e?.response?.data?.detail });
        setError(e?.response?.data?.detail || "Could not open this consent request");
      });
  }, []);

  return (
    <WalletShell activeTab="settings">
      <div className="max-w-md mx-auto px-4 py-16" data-testid="consent-claim-page">
        {tokenMissing && (
          <ClaimError title="Missing link">
            This page needs a valid consent link. Please open the link the institution sent to you again.
          </ClaimError>
        )}
        {error && (
          <ClaimError title="This link didn't work">
            {error}
            <div className="mt-4">
              <Link to="/wallet/consent" className="text-sm font-semibold underline" style={{ color: NAVY }}>
                Go to My Consent
              </Link>
            </div>
          </ClaimError>
        )}
        {!error && !tokenMissing && (
          <div className="text-center space-y-4" data-testid="consent-claim-loading">
            <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center" style={{ background: `${GOLD}22` }}>
              <ShieldCheck className="w-8 h-8" style={{ color: NAVY }} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Verifying request</div>
            <div className="font-display text-xl font-semibold" style={{ color: NAVY }}>Opening the consent request…</div>
            <div className="text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> One moment
            </div>
          </div>
        )}
      </div>
    </WalletShell>
  );
}

function ClaimError({ title, children }) {
  return (
    <div className="rounded-2xl bg-rose-50 border border-rose-200 p-6 text-sm text-rose-900" data-testid="consent-claim-error">
      <div className="flex items-center gap-2 font-semibold mb-2">
        <AlertTriangle className="w-4 h-4" />
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
